// homesCalc.js — Homes "best house" ranking, price-drop detection and the
// card's display strings. Pure: no React, no network, no clock unless one is
// passed in (`today`). Every function takes Homes_Watch rows as figApi returns
// them ({ id, ...fields }).
//
// Ported from sharepoint-mcp/homes-trend.js (dates, history, computed values)
// so the card and the server agree. One deliberate difference, agreed for P2:
// total change is measured from the PEAK price (the highest of FirstPrice,
// OriginalListPrice and every PriceHistory price), and the drop badge and the
// trend line both use that same number.
//
// NULL VS ZERO: unknown is null and is never shown or scored as 0. A factor with
// no data is left out of the FitScore, never averaged in as 0.

// ── constants ────────────────────────────────────────────────────────────────

// The eight factors are defined below (FACTORS), after the per-row helpers
// their compute functions use.

// Weights for Fit (sum 100). A factor with no data is skipped and the rest
// renormalize; a weight of 0 drops the factor from Fit entirely. Custom mode
// ignores these and weights its selected factors equally.
export const FIT_WEIGHTS = { commute: 22, newness: 18, price: 15, patio: 13, size: 10, garage: 9, trails: 7, family: 6 };

// Commute is scored on a fixed scale, not against the other houses:
// COMMUTE_BEST_HOURS a week or less = 100, COMMUTE_WORST_HOURS or more = 0.
export const COMMUTE_BEST_HOURS = 2;
export const COMMUTE_WORST_HOURS = 8;

// True monthly cost assumptions. CommuteHoursWeek was stored as miles ÷ MPH,
// so miles/week = hours × MPH. MILE_COST is an assumption (shown on the card).
export const MPH = 30;
export const MILE_COST = 0.70;

// Swim program (2026-10-10). The kids' club is undecided; the server scores the
// commute once per program into CommuteByProgramJson
// ({"TWST":{miles,hours,splitMiles,splitHours,note},…,"HEDGE":{…,worstMiles,weights}}).
// The picked program feeds the commute bar, the Fit commute term, true cost and
// the True cost sort. CommuteHoursWeek / CommuteNote stay the TWST values.
export const PROGRAMS = [
  { key: 'TWST',     label: 'TWST' },
  { key: 'MARC',     label: 'MONTGOMERY' },
  { key: 'CATS',     label: 'CONROE' },
  { key: 'MAGNOLIA', label: 'MAGNOLIA' },
  { key: 'HEDGE',    label: 'HEDGE' },
];
export const PROGRAM_KEYS = PROGRAMS.map((p) => p.key);
export const DEFAULT_PROGRAM = 'TWST';
/** A saved or passed program key; anything else → TWST. */
export const normalizeProgram = (v) => (PROGRAM_KEYS.includes(v) ? v : DEFAULT_PROGRAM);

export const SIZE_CAP_SQFT = 4000;   // size score rises linearly to this, flat above
export const NEW_DROP_DAYS = 7;      // a drop this recent gets the NEW DROP flag

// The house is leased or gone from the market — what "Clear inactive" archives.
// The server freezes only on leased/rented (homes-trend.js LEASED_STATUS_RE).
export const INACTIVE_STATUS_RE = /leased|rented|off[\s-]?market|withdrawn|expired|terminated|cancel/i;

const TZ = 'America/Chicago';
const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86_400_000;

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const known = (v) => v !== undefined && v !== null && v !== '';
const num = (v) => (isNum(v) ? v : null);

// ── dates (port of homes-trend.js) ───────────────────────────────────────────

/** Calendar day (Chicago) as 'YYYY-MM-DD', or null. SharePoint dateOnly read-backs (T07/T08:00Z) are sliced. */
export function toYmd(value) {
  if (!known(value)) return null;
  if (typeof value === 'string' && YMD_RE.test(value)) return value;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T0[78]:00:00(\.000)?Z$/.test(value)) return value.slice(0, 10);
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}
const dayNum = (ymd) => Date.UTC(+ymd.slice(0, 4), +ymd.slice(5, 7) - 1, +ymd.slice(8, 10)) / DAY_MS;

/** Whole days from a to b, or null if either is unknown. */
export function daysBetween(a, b) {
  const x = toYmd(a), y = toYmd(b);
  return x && y ? dayNum(y) - dayNum(x) : null;
}

/** PriceHistory column → array, oldest first. Malformed → []. */
export function parseHistory(value) {
  let a = value;
  if (!Array.isArray(a)) {
    if (!known(a)) return [];
    try { a = JSON.parse(a); } catch { return []; }
    if (!Array.isArray(a)) return [];
  }
  return a
    .map((e) => ({ ...e, date: toYmd(e?.date) }))
    .filter((e) => e.date)
    .sort((p, q) => (p.date < q.date ? -1 : p.date > q.date ? 1 : 0));
}

// ── per-row values ───────────────────────────────────────────────────────────

export const isWatch = (r) => (r?.Role ?? 'Watch') === 'Watch';
export const isArchived = (r) => r?.WatchStatus === 'Archived';
export const isInactive = (r) => known(r?.LeaseStatus) && INACTIVE_STATUS_RE.test(String(r.LeaseStatus));

export function pricePerSqFt(r) {
  const p = num(r?.LeasePrice), s = num(r?.SqFt);
  return p !== null && s && s > 0 ? Math.round((p / s) * 100) / 100 : null;
}

/** RenovatedYear when set, else YearBuilt; null when neither is known. */
export function newnessYear(r) {
  const ren = num(r?.RenovatedYear), built = num(r?.YearBuilt);
  if (ren !== null && ren > 0) return ren;
  return built !== null && built > 0 ? built : null;
}

/** Highest price this house has asked: FirstPrice, OriginalListPrice, any history price. */
export function peakPrice(r) {
  const ps = [num(r?.FirstPrice), num(r?.OriginalListPrice), ...parseHistory(r?.PriceHistory).map((e) => num(e.price))]
    .filter((p) => p !== null && p > 0);
  return ps.length ? Math.max(...ps) : null;
}

/** Consecutive price changes in history: [{ date, from, to, delta }], oldest first. */
export function priceChanges(r) {
  const priced = parseHistory(r?.PriceHistory).filter((e) => isNum(e.price));
  const out = [];
  for (let i = 1; i < priced.length; i++) {
    if (priced[i].price !== priced[i - 1].price) {
      out.push({ date: priced[i].date, from: priced[i - 1].price, to: priced[i].price, delta: priced[i].price - priced[i - 1].price });
    }
  }
  return out;
}

/**
 * Everything the drop badge, NEW DROP flag and rise badge need.
 *   dropped   current LeasePrice is below the peak
 *   amount    peak − current (positive), pct of peak (positive, 1 decimal)
 *   cuts      PriceCutCount when known, else the number of decreases in history; null when unknown
 *   lastDrop  the most recent decrease in history { date, amount } or null
 *   isNew     lastDrop is within NEW_DROP_DAYS of today
 *   rise      the most recent change was an increase: { date, amount } or null
 */
export function priceDrop(r, { today = new Date() } = {}) {
  const price = num(r?.LeasePrice);
  const peak = peakPrice(r);
  const changes = priceChanges(r);
  const dropped = price !== null && peak !== null && price < peak;
  const amount = dropped ? peak - price : null;
  const pct = dropped ? Math.round((amount / peak) * 1000) / 10 : null;
  const decreases = changes.filter((c) => c.delta < 0);
  const cuts = isNum(r?.PriceCutCount) ? r.PriceCutCount : (changes.length ? decreases.length : null);
  const last = decreases[decreases.length - 1] || null;
  const lastDrop = last ? { date: last.date, amount: -last.delta } : null;
  const ago = lastDrop ? daysBetween(lastDrop.date, today) : null;
  const isNew = ago !== null && ago >= 0 && ago <= NEW_DROP_DAYS;
  const latest = changes[changes.length - 1] || null;
  const rise = latest && latest.delta > 0 ? { date: latest.date, amount: latest.delta } : null;
  return { dropped, amount, pct, peak, cuts, lastDrop, lastDropDaysAgo: ago, isNew, rise };
}

/**
 * The day the days-on-market clock stops for a row with a status badge: the
 * first PriceHistory entry whose status matches that badge, else LeasedDate,
 * else today (still counting). null for a live row.
 */
export function stopDate(r, { today = new Date() } = {}) {
  const badge = statusBadge(r?.LeaseStatus);
  if (!badge) return null;
  const hit = parseHistory(r?.PriceHistory).find((e) => known(e.status) && BADGE_RE[badge.kind].test(String(e.status)));
  return hit ? hit.date : known(r?.LeasedDate) ? toYmd(r.LeasedDate) : toYmd(today);
}

/**
 * Port of homes-trend.js computed(), with total change measured from the peak.
 * daysOnMarket stops at stopDate() for a badged row (stopped: true).
 */
export function computed(r, { today = new Date() } = {}) {
  const price = num(r?.LeasePrice);
  const peak = peakPrice(r);
  const totalChange = price !== null && peak !== null ? price - peak : null;
  const stop = stopDate(r, { today });
  const end = stop ?? (known(r?.LeasedDate) ? r.LeasedDate : today);
  return {
    pricePerSqFt: pricePerSqFt(r),
    totalChange,
    totalChangePct: totalChange !== null && peak ? Math.round((totalChange / peak) * 1000) / 10 : null,
    daysOnMarket: known(r?.ListDate) ? daysBetween(r.ListDate, end) : null,
    stopped: stop !== null,
    daysSinceLastChange: known(r?.LastPriceChangeDate) ? daysBetween(r.LastPriceChangeDate, today) : null,
  };
}

// ── factors ──────────────────────────────────────────────────────────────────

/** 0–100 position of v among vals. Lower-is-better flips it. All equal (or one house) → 100. */
export function relScore(v, vals, { lowerBetter = false } = {}) {
  if (v === null || v === undefined) return null;
  const xs = vals.filter((x) => x !== null && x !== undefined);
  const min = Math.min(...xs), max = Math.max(...xs);
  if (!(max > min)) return 100;
  const t = lowerBetter ? (max - v) / (max - min) : (v - min) / (max - min);
  return Math.round(t * 1000) / 10;
}

const oneToFive = (s) => (isNum(s) && s >= 1 && s <= 5 ? Math.round(((s - 1) / 4) * 1000) / 10 : null);
const hoursOrNull = (v) => (isNum(v) && v >= 0 ? v : null);

/** CommuteByProgramJson → object, or null when absent or unreadable. */
export function parseByProgram(r) {
  const v = r?.CommuteByProgramJson;
  if (v && typeof v === 'object') return v;
  if (typeof v !== 'string' || !v.trim()) return null;
  try { const o = JSON.parse(v); return o && typeof o === 'object' && !Array.isArray(o) ? o : null; } catch { return null; }
}

/**
 * The commute for one program: { hours, miles, splitHours, note, entry, fallback }.
 * From CommuteByProgramJson when the house has it (a program the server could
 * not score stays null, with its note). A house not scored by program yet falls
 * back to the TWST columns; fallback is true when that stands in for another
 * program. hours is null for unknown, never 0.
 */
export function commuteFor(r, program = DEFAULT_PROGRAM) {
  const p = normalizeProgram(program);
  const entry = parseByProgram(r)?.[p];
  if (entry && typeof entry === 'object') {
    return { hours: hoursOrNull(entry.hours), miles: hoursOrNull(entry.miles), splitHours: hoursOrNull(entry.splitHours), note: entry.note ?? null, entry, fallback: false };
  }
  return { hours: hoursOrNull(r?.CommuteHoursWeek), miles: hoursOrNull(r?.CommuteMilesWeek), splitHours: hoursOrNull(r?.CommuteSplitHoursWeek), note: r?.CommuteNote ?? null, entry: null, fallback: p !== DEFAULT_PROGRAM };
}

/** Fixed scale: ≤ 2 h/wk = 100, ≥ 8 h/wk = 0, linear between; unknown = null. */
export function commuteScore(hours) {
  if (hours === null || hours === undefined) return null;
  const t = (COMMUTE_WORST_HOURS - hours) / (COMMUTE_WORST_HOURS - COMMUTE_BEST_HOURS);
  return Math.round(Math.max(0, Math.min(1, t)) * 1000) / 10;
}

/**
 * Garage text → { cars, tandem } or null. First integer = car count; the word
 * "tandem" (any case) = tandem. No integer ("", "Carport") = unknown.
 */
export function parseGarage(text) {
  const m = String(text ?? '').match(/\d+/);
  if (!m) return null;
  return { cars: Number(m[0]), tandem: /tandem/i.test(String(text)) };
}

/** 3+ cars not tandem = 100; 3+ tandem = 60; fewer than 3 = 0; unparseable = null. */
export function garageScore(text) {
  const g = parseGarage(text);
  if (!g) return null;
  if (g.cars >= 3) return g.tandem ? 60 : 100;
  return 0;
}

/** Values the relative factors need from the comparison set (the visible Watch rows). */
export function factorContext(set, { today = new Date(), program = DEFAULT_PROGRAM } = {}) {
  return {
    rents: set.map((r) => num(r.LeasePrice)),
    years: set.map(newnessYear),
    program: normalizeProgram(program),
  };
}

/**
 * The eight factors. compute(row, ctx) → 0–100, or null when the house has no
 * data for it (never 0 for unknown). ctx from factorContext().
 */
// Listed in FIT_WEIGHTS order (heaviest first): chips, bars and "unscored" text follow it.
export const FACTORS = [
  { key: 'commute', label: 'Commute', compute: (r, c) => commuteScore(commuteFor(r, c.program).hours) },
  { key: 'newness', label: 'Newness', compute: (r, c) => relScore(newnessYear(r), c.years) },
  { key: 'price',   label: 'Price',   compute: (r, c) => relScore(num(r.LeasePrice), c.rents, { lowerBetter: true }) },
  { key: 'patio',   label: 'Patio',   compute: (r) => oneToFive(r.PatioScore) },
  { key: 'size',    label: 'Size',    compute: (r) => {
    const sq = num(r.SqFt);
    return sq !== null && sq > 0 ? Math.round((Math.min(sq, SIZE_CAP_SQFT) / SIZE_CAP_SQFT) * 1000) / 10 : null;
  } },
  { key: 'garage',  label: 'Garage',  compute: (r) => garageScore(r.Garage) },
  { key: 'trails',  label: 'Trails',  compute: (r) => oneToFive(r.TrailScore) },
  { key: 'family',  label: 'Family',  compute: (r) => oneToFive(r.FamilyScore) },
];
export const FACTOR_KEYS = FACTORS.map((f) => f.key);
const LABEL = Object.fromEntries(FACTORS.map((f) => [f.key, f.label]));

/** Map(id → { price, size, newness, commute, trails, family, patio, garage }) for every row in `set`. */
export function subScores(set, { today = new Date(), program = DEFAULT_PROGRAM } = {}) {
  const ctx = factorContext(set, { today, program });
  return new Map(set.map((r) => [r.id, Object.fromEntries(FACTORS.map((f) => [f.key, f.compute(r, ctx)]))]));
}

/**
 * Weighted average over only the factors with data; the weights of the rest
 * renormalize away. { score|null, scored, of, partial, skipped: [keys] }.
 */
export function fitScore(subs, weights = FIT_WEIGHTS) {
  const active = FACTORS.filter((f) => (weights[f.key] ?? 0) > 0);
  let sum = 0, wsum = 0, scored = 0;
  const skipped = [];
  for (const f of active) {
    const v = subs?.[f.key];
    if (v === null || v === undefined) { skipped.push(f.key); continue; }
    sum += v * weights[f.key]; wsum += weights[f.key]; scored++;
  }
  return { score: wsum ? Math.round(sum / wsum) : null, scored, of: active.length, partial: scored < active.length, skipped };
}

/** "patio, garage unscored" for the skipped factor keys (FACTORS order), or null. */
export function unscoredText(skipped) {
  const names = FACTORS.filter((f) => (skipped || []).includes(f.key)).map((f) => f.label.toLowerCase());
  return names.length ? `${names.join(', ')} unscored` : null;
}

/**
 * Score mode. { kind: 'fit' } or { kind: 'custom', factors: [keys] }. A custom
 * mode with no valid factors is Fit. Unknown keys are dropped.
 */
export function normalizeMode(mode) {
  const factors = Array.isArray(mode?.factors) ? FACTOR_KEYS.filter((k) => mode.factors.includes(k)) : [];
  return mode?.kind === 'custom' && factors.length ? { kind: 'custom', factors } : { kind: 'fit', factors: [] };
}

/** "Fit", "Patio", or "Custom: Commute + Patio" (factor order as in FACTORS). */
export function modeLabel(mode) {
  const m = normalizeMode(mode);
  if (m.kind === 'fit') return 'Fit';
  const names = m.factors.map((k) => LABEL[k]);
  return names.length === 1 ? names[0] : `Custom: ${names.join(' + ')}`;
}

/** Score under a mode: Fit = FIT_WEIGHTS over all eight; Custom = plain average of the selected factors that have data. */
export function scoreFor(subs, mode) {
  const m = normalizeMode(mode);
  if (m.kind === 'fit') return fitScore(subs);
  return fitScore(subs, Object.fromEntries(m.factors.map((k) => [k, 1])));
}

/** True monthly cost: rent + driving for the program's commute. null when rent or that commute is unknown. */
export function trueCost(r, program = DEFAULT_PROGRAM) {
  const rent = num(r?.LeasePrice), hours = commuteFor(r, program).hours;
  if (rent === null || hours === null) return null;
  const milesPerWeek = hours * MPH;
  const driving = Math.round((milesPerWeek * MILE_COST * 52) / 12);
  return { rent, milesPerWeek: Math.round(milesPerWeek), driving, total: rent + driving };
}

/** VerifyNote → ["walk trail from door", "confirm street type"]. */
export const verifyItems = (note) => String(note ?? '').split(';').map((x) => x.trim()).filter(Boolean);

// ── the board ────────────────────────────────────────────────────────────────

/** The rows the dashboard ranks: Role Watch (absent Role counts as Watch), not archived. */
export const watchSet = (rows) => (rows || []).filter((r) => isWatch(r) && !isArchived(r));

/** Watch rows whose status says the house is gone — what "Clear inactive" archives. */
export const inactiveWatch = (rows) => watchSet(rows).filter(isInactive);

const mean = (xs) => (xs.length ? Math.round(xs.reduce((a, b) => a + b, 0) / xs.length) : null);
const median = (xs) => {
  if (!xs.length) return null;
  const a = [...xs].sort((p, q) => p - q), m = a.length >> 1;
  return a.length % 2 ? a[m] : Math.round((a[m - 1] + a[m]) / 2);
};

/**
 * Board tiles. Active = no status badge (Pending / Gone / Leased are not).
 * Days listed: avg + median live DOM over active rows, lost = avg stopped DOM
 * over badged rows. Rows without a ListDate are left out, never 0.
 */
export function summary(rows, { today = new Date() } = {}) {
  const set = watchSet(rows);
  const live = set.filter((r) => !statusBadge(r.LeaseStatus));
  const dom = (rs) => rs.map((r) => computed(r, { today }).daysOnMarket).filter((d) => d !== null);
  const liveDays = dom(live);
  return {
    watching: set.length,
    active: live.length,
    drops: set.filter((r) => priceDrop(r, { today }).dropped).length,
    forSale: set.filter((r) => r.ForSale === true).length,
    avgDays: mean(liveDays),
    medianDays: median(liveDays),
    lostDays: mean(dom(set.filter((r) => statusBadge(r.LeaseStatus)))),
  };
}

// Sort chips (single-select, optional). Score order is the default when none is on.
export const SORTS = [
  { key: 'drop',     label: 'Drop %' },
  { key: 'ppsf',     label: '$/sq ft' },
  { key: 'truecost', label: 'True cost' },
  { key: 'dom',      label: 'Days listed' },
  { key: 'found',    label: 'Newest found' },
  { key: 'opensoon', label: 'Open soon' },   // a filter too: only houses open now or opening within OPEN_SOON_HOURS
];
const SORT_KEYS = SORTS.map((x) => x.key);

const idCmp = (a, b) => String(a.row.HomeId ?? '').localeCompare(String(b.row.HomeId ?? ''));
// nulls always sort last; ties fall back to HomeId so the order is stable
const by = (get, dir) => (a, b) => {
  const x = get(a), y = get(b);
  if (x === null && y === null) return idCmp(a, b);
  if (x === null) return 1;
  if (y === null) return -1;
  if (x !== y) return dir * (x < y ? -1 : 1);
  return idCmp(a, b);
};

/**
 * Score order. Fit: by score, unscored last. Custom: houses with every
 * selected factor first, then partial ones, then "not scored" (none of them);
 * by score inside each group.
 */
function scoreCmp(mode) {
  const bucket = (v) => (v.score.score === null ? 2 : mode.kind === 'custom' && v.score.partial ? 1 : 0);
  return (a, b) => bucket(a) - bucket(b) || by((v) => v.score.score, -1)(a, b);
}

/**
 * View models for the cards. Rank is the score rank under `mode`, whatever the
 * sort. dropsOnly keeps dropped houses and sorts them by biggest % drop.
 */
export function rankHomes(rows, { mode, sort = null, dropsOnly = false, today = new Date(), program = DEFAULT_PROGRAM } = {}) {
  const m = normalizeMode(mode);
  const set = watchSet(rows);
  const subs = subScores(set, { today, program });
  let vms = set.map((row) => {
    const sub = subs.get(row.id);
    const score = scoreFor(sub, m);
    return { row, sub, score, fit: score, drop: priceDrop(row, { today }), calc: computed(row, { today }), cost: trueCost(row, program), commute: commuteFor(row, program) };
  });
  [...vms].sort(scoreCmp(m)).forEach((v, i) => { v.rank = i + 1; });
  let key = SORT_KEYS.includes(sort) ? sort : null;
  if (dropsOnly) { vms = vms.filter((v) => v.drop.dropped); key = 'drop'; }
  if (key === 'opensoon') {
    for (const v of vms) v.openSoon = openSoonWindow(v.row, today);
    vms = vms.filter((v) => v.openSoon);
  }
  const cmp = {
    drop: by((v) => (v.drop.dropped ? v.drop.pct : null), -1),
    ppsf: by((v) => v.calc.pricePerSqFt, 1),
    truecost: by((v) => v.cost?.total ?? null, 1),
    dom: by((v) => v.calc.daysOnMarket, -1),
    found: by((v) => toYmd(v.row.FoundDate), -1),
    opensoon: by((v) => v.openSoon.startMs, 1),
  }[key] || scoreCmp(m);
  return vms.sort(cmp);
}

// ── open houses (2026-10-10) ─────────────────────────────────────────────────
// OpenHousesJson = [{start, end, source, sourceRef}], ISO instants with the
// Chicago offset, written by the server from HAR mail. Past windows are kept as
// history; the board only lights up for a window whose end is still ahead.
export const OPEN_SOON_HOURS = 48;
const HOUR_MS = 3_600_000;

/** Valid windows, oldest first: [{ start, end, startMs, endMs, source, sourceRef }]. Unreadable → []. */
export function openHouses(r) {
  let list;
  try { list = typeof r?.OpenHousesJson === 'string' ? JSON.parse(r.OpenHousesJson) : r?.OpenHousesJson; } catch { return []; }
  if (!Array.isArray(list)) return [];
  return list
    .map((w) => ({ ...w, startMs: Date.parse(w?.start), endMs: Date.parse(w?.end) }))
    .filter((w) => Number.isFinite(w.startMs) && Number.isFinite(w.endMs) && w.endMs > w.startMs)
    .sort((a, b) => a.startMs - b.startMs);
}

/**
 * Open-house state at `now`: { now: window | null (inside it), next: the
 * soonest window not yet over (may be the open one), active: any window still
 * ahead (drives the green dot) }.
 */
export function openHouseState(r, now = new Date()) {
  const t = (now instanceof Date ? now : new Date(now)).getTime();
  const ahead = openHouses(r).filter((w) => w.endMs > t);
  return { now: ahead.find((w) => w.startMs <= t) || null, next: ahead[0] || null, active: ahead.length > 0 };
}

/** The window that puts a house in "Open soon": open now, or starting within OPEN_SOON_HOURS. */
export function openSoonWindow(r, now = new Date()) {
  const t = (now instanceof Date ? now : new Date(now)).getTime();
  return openHouses(r).find((w) => w.endMs > t && w.startMs <= t + OPEN_SOON_HOURS * HOUR_MS) || null;
}

const chicagoParts = (ms) => Object.fromEntries(new Intl.DateTimeFormat('en-US', {
  timeZone: TZ, weekday: 'short', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
}).formatToParts(new Date(ms)).map((p) => [p.type, p.value]));
const clock = (p, withPeriod) => `${p.hour}${p.minute === '00' ? '' : `:${p.minute}`}${withPeriod ? ` ${p.dayPeriod}` : ''}`;

/**
 * "Sun 1–3 PM" in Chicago time; "11 AM–1 PM" when the halves differ; the date
 * is added ("Sun 10/18 1–3 PM") when the window is 6 or more days from `now`.
 */
export function openHouseText(w, now = new Date()) {
  const a = chicagoParts(w.startMs), b = chicagoParts(w.endMs);
  const far = w.startMs - (now instanceof Date ? now : new Date(now)).getTime() >= 6 * 24 * HOUR_MS;
  const day = `${a.weekday}${far ? ` ${a.month}/${a.day}` : ''}`;
  const same = a.dayPeriod === b.dayPeriod;
  return `${day} ${clock(a, !same)}–${clock(b, true)}`;
}

/** "until 5 PM" for an open window. */
export const openUntilText = (w) => `until ${clock(chicagoParts(w.endMs), true)}`;

// ── vs #1, tie tiers, program swing (2026-10-10) ─────────────────────────────
// The benchmark is whichever house is first in the current view (program +
// Score + Sort + drops filter). Every other card says what picking it instead
// costs, in real units. Retune the bands here.
export const TIE_BAND = 5;        // Fit points behind #1, inclusive: "≈ #1"
export const CLOSE_BAND = 12;     // more than TIE_BAND up to this, inclusive: "−N" amber; beyond: gray, dimmed
export const SWING_STEADY_SHARE = 0.20;   // "Any club" when best vs worst program rank ≤ max(2, round(share × houses ranked))
export const SWING_LABELS = { steady: 'Any club', swingy: 'Depends on club' };
// short = the card strip; tiny / letter = the row boxes (letter at phone width); name = tooltips.
export const SWING_PROGRAMS = [
  { key: 'TWST',     short: 'TWST',   tiny: 'TWST', letter: 'T', name: 'TWST' },
  { key: 'MARC',     short: 'MONT',   tiny: 'MONT', letter: 'M', name: 'Montgomery' },
  { key: 'CATS',     short: 'CONROE', tiny: 'CON',  letter: 'C', name: 'Conroe' },
  { key: 'MAGNOLIA', short: 'MAG',    tiny: 'MAG',  letter: 'G', name: 'Magnolia' },
];

const round5 = (n) => Math.round(n / 5) * 5;
const roundTenth = (n) => Math.round(n * 10) / 10;
const scoredCommute = (c) => (c && c.hours !== null && !c.fallback ? c.hours : null);
const signed = (n) => (n > 0 ? '+' : n < 0 ? '−' : '±');
const toneOf = (n) => (n < 0 ? 'better' : n > 0 ? 'worse' : 'same');   // every term: lower is better

/**
 * The vs-#1 terms for one card: [{ key, delta, text, short, tone }], in order
 * hours, rent, true cost (short = the compact-row form). Hours to 0.1, dollars
 * to the nearest $5; tone is from the rounded value. The hours term also
 * carries miles (whole-mile gap, unsigned context shown in gray; null when
 * either side has no miles). A term either side cannot compute (no rent, no
 * commute scored for this program) is left out, never shown as 0.
 */
export function vsTerms(vm, bench) {
  const out = [];
  const h = scoredCommute(vm.commute), hb = scoredCommute(bench.commute);
  if (h !== null && hb !== null) {
    const d = roundTenth(h - hb) || 0;
    const m = isNum(vm.commute.miles) && isNum(bench.commute.miles) ? Math.abs(Math.round(vm.commute.miles - bench.commute.miles)) : null;
    const hrs = `${signed(d)}${Math.abs(d).toFixed(1)}`;
    out.push({ key: 'hours', delta: d, text: `${hrs} h/wk`, short: `${hrs} h`, tone: toneOf(d), miles: m, milesText: m === null ? null : `${m} mi` });
  }
  const rent = num(vm.row?.LeasePrice), rentB = num(bench.row?.LeasePrice);
  if (rent !== null && rentB !== null) {
    const d = round5(rent - rentB) || 0;
    out.push({ key: 'rent', delta: d, text: `${signed(d)}${money(d)}/mo`, short: `${signed(d)}${money(d)}`, tone: toneOf(d) });
  }
  if (vm.cost && bench.cost && h !== null && hb !== null) {
    const d = round5(vm.cost.total - bench.cost.total) || 0;
    out.push({ key: 'truecost', delta: d, text: `true cost ${signed(d)}${money(d)}/mo`, short: `true ${signed(d)}${money(d)}`, tone: toneOf(d) });
  }
  return out;
}

/** Tie tier from Fit points behind #1: 'tied' (≤ TIE_BAND), 'close' (≤ CLOSE_BAND), 'behind'; null when unscored. */
export function tierOf(behind) {
  if (!isNum(behind)) return null;
  if (behind <= TIE_BAND) return 'tied';
  if (behind <= CLOSE_BAND) return 'close';
  return 'behind';
}

/** Largest best-vs-worst rank spread that still reads steady, for a list of n houses. */
export const swingAllowance = (n) => Math.max(2, Math.round(SWING_STEADY_SHARE * n));

/**
 * 'steady' / 'swingy' from the program ranks (nulls ignored) in a list of n
 * ranked houses; null with fewer than two ranks. Shown via SWING_LABELS.
 */
export function swingLabel(ranks, n) {
  const known = Object.values(ranks || {}).filter(isNum);
  if (known.length < 2) return null;
  return Math.max(...known) - Math.min(...known) <= swingAllowance(n) ? 'steady' : 'swingy';
}

/** The benchmark of a view: its first RENTABLE house (a Pending / Off Market / Leased one keeps its place but never benchmarks). */
export const benchmarkOf = (vms) => vms.find((v) => isRentable(v.row)) || null;

/**
 * The board: rankHomes' view models in display order, each with
 *   benchmark  true on the first rentable card (benchmarkOf)
 *   vs         vsTerms against the benchmark; null on it and on non-rentable cards
 *   tier       { kind, behind } on Fit with no sort or drops filter, rentable cards only, else null
 *   swing      { ranks: { TWST, MARC, CATS, MAGNOLIA }, label } — this house's
 *              place under each program with the same Score / Sort / filter;
 *              null where that program has no commute score for it
 * and dividerAfter = the row id of the last tied card after #1 (or null).
 */
export function boardView(rows, { mode, sort = null, dropsOnly = false, today = new Date(), program = DEFAULT_PROGRAM } = {}) {
  const opts = { mode, sort, dropsOnly, today };
  const vms = rankHomes(rows, { ...opts, program });
  const bench = benchmarkOf(vms);
  const tiers = normalizeMode(mode).kind === 'fit' && !SORT_KEYS.includes(sort) && !dropsOnly && isNum(bench?.score.score);
  const place = Object.fromEntries(SWING_PROGRAMS.map(({ key }) => [key, new Map(rankHomes(rows, { ...opts, program: key }).map((v, i) => [v.row.id, i + 1]))]));
  let dividerAfter = null;
  for (const vm of vms) {
    const rentable = isRentable(vm.row);
    vm.benchmark = vm === bench;
    vm.vs = vm === bench || !rentable || !bench ? null : vsTerms(vm, bench);
    const behind = tiers && rentable && isNum(vm.score.score) ? bench.score.score - vm.score.score : null;
    vm.tier = behind === null ? null : { kind: tierOf(behind), behind };
    if (vm !== bench && vm.tier?.kind === 'tied') dividerAfter = vm.row.id;
    const ranks = Object.fromEntries(SWING_PROGRAMS.map(({ key }) => [key, scoredCommute(commuteFor(vm.row, key)) === null ? null : place[key].get(vm.row.id) ?? null]));
    vm.swing = { ranks, label: swingLabel(ranks, vms.length) };
  }
  return { vms, dividerAfter, bench };
}

/** "6 watching · 2 price drops · top: 10053 Preserve Way (69)" parts, for the collapsed header. */
export function headerSummary(rows, { mode, sort = null, dropsOnly = false, today = new Date(), program = DEFAULT_PROGRAM } = {}) {
  const s = summary(rows, { today });
  // top = the board's Benchmark (same view, same rentable rule), so the two always agree
  const top = benchmarkOf(rankHomes(rows, { mode, sort, dropsOnly, today, program }));
  return { watching: s.watching, drops: s.drops, top: top ? { title: top.row.Title || top.row.HomeId, score: top.score.score } : null };
}

// ── display strings ──────────────────────────────────────────────────────────

/**
 * More-panel commute lines for the picked program. A program: its note lines.
 * HEDGE: one line per program (miles/wk and hours), the worst case, and the
 * weights in use. Nothing scored → [].
 */
export function commuteLines(r, program = DEFAULT_PROGRAM) {
  const p = normalizeProgram(program);
  const c = commuteFor(r, p);
  if (p !== 'HEDGE' || !c.entry) return String(c.note ?? '').split('\n').filter(Boolean);
  const all = parseByProgram(r);
  const lines = PROGRAMS.filter((x) => x.key !== 'HEDGE').map((x) => {
    const e = all[x.key];
    return isNum(e?.miles) ? `${x.label} ${e.miles} mi/wk = ${e.hours} h` : `${x.label} not scored`;
  });
  if (isNum(c.entry.worstMiles)) lines.push(`Worst case ${c.entry.worstMiles} mi/wk`);
  else if (c.entry.note) lines.push(c.entry.note);
  const w = c.entry.weights;
  if (w && typeof w === 'object') lines.push(`Weights: ${PROGRAMS.filter((x) => x.key !== 'HEDGE').map((x) => `${x.label} ${w[x.key]}`).join(' · ')}`);
  return lines;
}

export const money = (n) => `$${Math.round(Math.abs(n)).toLocaleString('en-US')}`;

/** 'Sep 12' (adds the year when it is not today's year). */
export function shortDate(ymd, { today = new Date() } = {}) {
  const d = toYmd(ymd);
  if (!d) return null;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const s = `${months[+d.slice(5, 7) - 1]} ${+d.slice(8, 10)}`;
  return d.slice(0, 4) === (toYmd(today) || '').slice(0, 4) ? s : `${s}, ${d.slice(0, 4)}`;
}

/** "↓ $300 (−8%)" or null. */
export const dropBadgeText = (drop) => (drop?.dropped ? `↓ ${money(drop.amount)} (−${drop.pct}%)` : null);

/** "dropped $200 on Oct 6" or null. */
export const newDropText = (drop, opts) => (drop?.isNew && drop.lastDrop ? `dropped ${money(drop.lastDrop.amount)} on ${shortDate(drop.lastDrop.date, opts)}` : null);

const agoText = (n) => (n === 0 ? 'today' : n === 1 ? '1 day ago' : `${n} days ago`);

/**
 * "Listed Sep 12 · 39 days · cut 2×, −$300 (−8%) · last cut 6 days ago".
 * Unknown parts are omitted; nothing is ever shown as 0.
 */
export function trendLine(r, { today = new Date() } = {}) {
  const c = computed(r, { today });
  const d = priceDrop(r, { today });
  const parts = [];
  const listed = shortDate(r?.ListDate, { today });
  const plural = (n) => `${n} day${n === 1 ? '' : 's'}`;
  if (listed) parts.push(`Listed ${listed}`);
  if (c.stopped) {
    if (c.daysOnMarket !== null && c.daysOnMarket >= 0) parts.push(`lasted ${plural(c.daysOnMarket)}`);
    else if (!listed && statusBadge(r?.LeaseStatus).kind !== 'pending') parts.push('gone before listing');
  } else if (c.daysOnMarket > 0) parts.push(plural(c.daysOnMarket));
  const cut = [];
  if (d.cuts > 0) cut.push(`cut ${d.cuts}×`);
  if (d.dropped) cut.push(`−${money(d.amount)} (−${d.pct}%)`);
  if (cut.length) parts.push(cut.join(', '));
  if (d.lastDrop && d.lastDropDaysAgo !== null && d.lastDropDaysAgo >= 0) parts.push(`last cut ${agoText(d.lastDropDaysAgo)}`);
  return parts.join(' · ');
}

/** Price timeline for the expanded card, newest first. */
export const timeline = (r) => parseHistory(r?.PriceHistory).reverse();

/** "413" from "413 Brackendale Ct" — the chart label. */
export const streetNumber = (title) => (String(title ?? '').match(/^\s*(\d+[A-Za-z]?)/) || [])[1] || String(title ?? '').slice(0, 6);

/** Lease status pill tone. */
export function statusTone(status) {
  const s = String(status ?? '').toLowerCase();
  if (!s) return null;
  if (INACTIVE_STATUS_RE.test(s)) return 'gone';
  if (/pending|option|contract/.test(s)) return 'pending';
  if (/coming/.test(s)) return 'soon';
  return 'active';
}

// Card badge for a house that is no longer simply available. Separate from
// isInactive / "Clear inactive" on purpose: Pending gets a badge but is never
// archived. Leased/rented rows usually become Comps and leave the board.
const LEASED_BADGE_RE = /\b(leased|rented)\b/i;
const GONE_BADGE_RE = /off[\s-]?market|withdrawn|expired|terminated|cancel/i;
const PENDING_BADGE_RE = /pending|under[\s-]?contract/i;
const BADGE_RE = { leased: LEASED_BADGE_RE, gone: GONE_BADGE_RE, pending: PENDING_BADGE_RE };

/** { kind: 'leased'|'gone'|'pending', label } from LeaseStatus, or null. */
/** Can the family still rent it? Pending / Under Contract, Off Market & co, and Leased are not. */
export const isRentable = (r) => !statusBadge(r?.LeaseStatus);

export function statusBadge(status) {
  const s = String(status ?? '');
  if (!s.trim()) return null;
  if (LEASED_BADGE_RE.test(s)) return { kind: 'leased', label: 'Leased' };
  if (GONE_BADGE_RE.test(s)) return { kind: 'gone', label: 'Gone' };
  if (PENDING_BADGE_RE.test(s)) return { kind: 'pending', label: 'Pending' };
  return null;
}

/** Fit color for chart dots and the score number: gray (0) → green (100). */
export function fitColor(score) {
  if (score === null || score === undefined) return '#b8b0c0';
  const t = Math.max(0, Math.min(1, score / 100));
  const from = [184, 176, 192], to = [46, 125, 50];
  const c = from.map((f, i) => Math.round(f + (to[i] - f) * t));
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
}

// ── photos (Homes_Photos library) ────────────────────────────────────────────

const HOME_ID_RE = /^HM-\d{5}$/;

/**
 * File name for a new photo: HM-00001.jpg when the house has none, otherwise
 * HM-00001__altN.ext (N = 1 + highest existing alt). Never overwrites.
 */
export function nextHomePhotoName(homeId, existingNames, ext) {
  if (!HOME_ID_RE.test(String(homeId || ''))) throw new Error('This house has no HomeId yet.');
  if (ext !== 'jpg' && ext !== 'png') throw new Error(`Unsupported photo extension: ${ext}`);
  const re = new RegExp(`^${homeId}(__alt(\\d+))?\\.(jpg|jpeg|png)$`, 'i');
  let hasMain = false, max = 0;
  for (const n of existingNames || []) {
    const m = re.exec(n);
    if (!m) continue;
    if (!m[1]) hasMain = true; else max = Math.max(max, Number(m[2]));
  }
  return hasMain || max > 0 ? `${homeId}__alt${max + 1}.${ext}` : `${homeId}.${ext}`;
}
